import React from "react";
import { shortDate } from "./dutyListModel";
import { usePaged, DutyListPager } from "./DutyListPager";
import EmployeeAvatar from "../../reports/oneStop/components/EmployeeAvatar";
import { useEmployeePhotoCache } from "../../masters/employees/useEmployeePhotoCache";

// The four list views of the Duty List page. Plain tables - the same data
// also goes to print and Excel, so what is on screen is what gets printed.
// Each is paged on screen only; print and Excel take every filtered line.

const Empty = ({ children }) => <div className="text-center text-muted py-5">{children}</div>;

const Kpi = ({ label, value, sub, tone }) => (
  <div className="duty-kpi">
    <div className={`duty-kpi-value ${tone || ""}`}>{value}</div>
    <div className="duty-kpi-label">{label}</div>
    {sub && <div className="duty-kpi-sub">{sub}</div>}
  </div>
);

export const DutyListTotals = ({ totals }) => (
  <div className="duty-kpi-row">
    <Kpi label="Customers" value={totals.customers} />
    <Kpi label="Day slots" value={totals.daySlots} tone="day" />
    <Kpi label="Night slots" value={totals.nightSlots} tone="night" />
    <Kpi label="Permanent" value={totals.permSlots} />
    <Kpi label="Temp" value={totals.tempSlots} tone={totals.tempSlots ? "temp" : ""} />
    <Kpi label="Full month" value={totals.fullMonthSlots} />
    <Kpi label="Part month" value={totals.partSlots} sub={`${totals.partShifts} shifts`} />
    <Kpi label="Shifts worked" value={totals.dayShifts + totals.nightShifts} sub={`${totals.dayShifts} Day · ${totals.nightShifts} Night`} />
    <Kpi label="Blank shifts" value={totals.blankShifts} tone={totals.blankShifts ? "blank" : ""} />
    <Kpi label="Staff used" value={totals.guards} sub="different people, relievers included" />
  </div>
);

// "Yes" = every slot of the shift covered all month (invoice as normal);
// "n of N" = some were not, and Part month says why; "-" = no such slot.
const FullCell = ({ text, first }) => (
  <td
    className={`text-center duty-num duty-col-full ${first ? "duty-grp" : ""} ${
      text === "Yes" ? "fw-bold text-success" : text === "-" ? "text-muted" : "fw-semibold text-warning-emphasis"
    }`}
  >
    {text}
  </td>
);

// Why a slot missed full month: the post itself ran part of the month
// (temp / new / ended / switched off) or it ran all month but days were not
// marked - the two things the Summary is used to tell apart.
export const PartTags = ({ tags }) =>
  tags.map((t) => (
    <span key={t.label} className={`duty-tag ${t.kind}`}>
      {t.label}
    </span>
  ));

export const DutyListSummaryTable = ({ summary, onPickCustomer }) => {
  const paged = usePaged(summary);
  if (summary.length === 0) return <Empty>No assignments match these filters.</Empty>;

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover duty-table duty-summary-table">
          <thead>
            <tr className="duty-group-head">
              <th rowSpan={2}>#</th>
              <th rowSpan={2} className="duty-cust">Customer</th>
              <th rowSpan={2}>Area</th>
              <th colSpan={2} className="duty-grp">Slots</th>
              <th colSpan={2} className="duty-grp">Assignment</th>
              <th colSpan={2} className="duty-grp duty-col-full">Full month</th>
              <th rowSpan={2} className="duty-grp">Part month - why</th>
              <th colSpan={2} className="duty-grp">Shifts worked</th>
              <th rowSpan={2} className="text-end duty-grp">Blank</th>
              <th
                rowSpan={2}
                className="text-end"
                title="Different people who covered these posts this month - relievers and replacements included, so it is usually more than Day + Night slots"
              >
                Staff used
              </th>
            </tr>
            <tr>
              <th className="text-end duty-num duty-grp">Day</th>
              <th className="text-end duty-num">Night</th>
              <th className="text-end duty-num duty-grp">Perm</th>
              <th className="text-end duty-num">Temp</th>
              <th className="text-center duty-num duty-grp duty-col-full">Day</th>
              <th className="text-center duty-num duty-col-full">Night</th>
              <th className="text-end duty-num duty-grp">Day</th>
              <th className="text-end duty-num">Night</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((s, i) => (
              <tr key={s.custId} role="button" onClick={() => onPickCustomer(s.custId)} title="Open this customer in the Detail view">
                <td className="text-muted">{paged.start + i + 1}</td>
                <td className="fw-semibold duty-cust">{s.custName}</td>
                <td>
                  {/* Some customers span many areas; the full list is in the tooltip. */}
                  <div className="duty-trim" title={s.areas}>
                    {s.areas}
                  </div>
                </td>
                <td className="text-end duty-num duty-grp">{s.daySlots}</td>
                <td className="text-end duty-num">{s.nightSlots}</td>
                <td className="text-end duty-num duty-grp">{s.permSlots}</td>
                <td className={`text-end duty-num ${s.tempSlots ? "fw-semibold text-warning-emphasis" : "text-muted"}`}>{s.tempSlots}</td>
                <FullCell text={s.fullDayText} first />
                <FullCell text={s.fullNightText} />
                <td className="small duty-wrap duty-grp">
                  {s.partMonth.length ? (
                    s.partMonth.map((p) => (
                      <div key={p.full} className="duty-part-line">
                        {p.text} <PartTags tags={p.tags} />
                      </div>
                    ))
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td className="text-end duty-num duty-grp">{s.dayShifts}</td>
                <td className="text-end duty-num">{s.nightShifts}</td>
                <td className={`text-end duty-grp ${s.blankShifts ? "text-danger fw-semibold" : "text-muted"}`}>{s.blankShifts}</td>
                <td className="text-end" title={`${s.guards} different people covered ${s.daySlots + s.nightSlots} slot${s.daySlots + s.nightSlots === 1 ? "" : "s"} - relievers included`}>
                  {s.guards}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DutyListPager paged={paged} />
    </>
  );
};

// Named "Employees", not "Guards": the list is everyone with duty on the
// filtered lines, office staff included.
export const DutyListGuardsTable = ({ guardRows, onPickGuard }) => {
  const paged = usePaged(guardRows);
  // Photos for the visible page only - a branch month has 400+ people.
  const photoUrls = useEmployeePhotoCache(paged.pageRows);
  if (guardRows.length === 0) return <Empty>No employees on duty for these filters.</Empty>;

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover duty-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Code</th>
              <th>Employee</th>
              <th className="text-end">Day</th>
              <th className="text-end">Night</th>
              <th className="text-end">Total</th>
              <th>Customers</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((g, i) => (
              <tr key={g.empCode} role="button" onClick={() => onPickGuard(g.empCode)} title="Show this employee's month">
                <td className="text-muted">{paged.start + i + 1}</td>
                <td className="fw-semibold text-primary">{g.empCode}</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <EmployeeAvatar name={g.name} photoUrl={photoUrls[g.employeeRecordGuid]} />
                    {g.name || <span className="text-muted">(not in employee master)</span>}
                  </div>
                </td>
                <td className="text-end">{g.dayShifts}</td>
                <td className="text-end">{g.nightShifts}</td>
                <td className="text-end fw-semibold">{g.total}</td>
                <td className="small">
                  {g.customerCount > 1 && <span className="badge bg-light text-dark border me-1">{g.customerCount}</span>}
                  {g.customerList}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DutyListPager paged={paged} />
    </>
  );
};

export const DutyListNewTable = ({ rows }) => {
  const paged = usePaged(rows);
  if (rows.length === 0) return <Empty>No assignment started in this month.</Empty>;

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover duty-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Customer</th>
              <th>Area</th>
              <th>Assignment</th>
              <th>Shift</th>
              <th>From</th>
              <th>To</th>
              <th>Status</th>
              <th className="text-end">Days worked</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((r, i) => (
              <tr key={r.key}>
                <td className="text-muted">{paged.start + i + 1}</td>
                <td className="fw-semibold">{r.custName}</td>
                <td>{r.area}</td>
                <td>{r.assignmentName}</td>
                <td>{r.shift}</td>
                <td>{shortDate(r.fromDate)}</td>
                <td>{r.toDate ? shortDate(r.toDate) : <span className="text-muted">Until further notice</span>}</td>
                <td>
                  {r.isTemp && <span className="badge bg-warning text-dark me-1">Temp</span>}
                  {r.isActive ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Switched off</span>}
                </td>
                <td className="text-end">{r.filledDays}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DutyListPager paged={paged} />
    </>
  );
};

export const DutyListBlankTable = ({ rows }) => {
  const paged = usePaged(rows);
  if (rows.length === 0) return <Empty>Every due shift has a guard for these filters.</Empty>;

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover duty-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Customer</th>
              <th>Area</th>
              <th>Assignment</th>
              <th>Shift</th>
              <th className="text-end">Blank</th>
              <th>Dates (day of month)</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((r, i) => (
              <tr key={r.key}>
                <td className="text-muted">{paged.start + i + 1}</td>
                <td className="fw-semibold">{r.custName}</td>
                <td>{r.area}</td>
                <td>
                  {r.assignmentName}
                  {r.isTemp && <span className="badge bg-warning text-dark ms-1">Temp</span>}
                </td>
                <td>{r.shift}</td>
                <td className="text-end text-danger fw-semibold">{r.blankInWindow.length}</td>
                <td className="small">{r.blankLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DutyListPager paged={paged} />
    </>
  );
};
